import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import {
  AssessmentResponseStatus,
  useAssessmentHandlers,
} from '../hooks/useAssessmentHandlers';
import IndividualAssessment from '@/modules/assessments/components/IndividualAssessment';
import useEnrollmentDashboardContext from '@/modules/enrollment/hooks/useEnrollmentDashboardContext';
import { FormActionTypes } from '@/modules/form/types';
import { DashboardEnrollment } from '@/modules/hmis/types';
import { cache } from '@/providers/apolloClient';
import { EnrollmentDashboardRoutes } from '@/routes/routes';
import {
  EnrolledClientFieldsFragment,
  FormDefinitionFieldsFragment,
  FullAssessmentFragment,
} from '@/types/gqlTypes';
import { generateSafePath } from '@/utils/pathEncoding';

interface Props {
  enrollment: DashboardEnrollment;
  client: EnrolledClientFieldsFragment;
  viewingDefinition: FormDefinitionFieldsFragment;
  editingDefinition: FormDefinitionFieldsFragment;
  assessment?: FullAssessmentFragment;
}

/**
 * Wraps IndividualAssessment for creating/editing/viewing an assessment.
 * This is only used for individual assessment pages, NOT for household assessment.
 */
const IndividualAssessmentFormController: React.FC<Props> = ({
  client,
  enrollment,
  viewingDefinition,
  editingDefinition,
  assessment,
}) => {
  const { handleOpenDesktopMenu } = useEnrollmentDashboardContext();
  const navigate = useNavigate();

  const navigateToEnrollment = useCallback(() => {
    navigate(
      generateSafePath(EnrollmentDashboardRoutes.ASSESSMENTS, {
        enrollmentId: enrollment.id,
        clientId: client.id,
      })
    );
    // Assessment is always displayed in 'focus mode', so re-expand desktop nav when navigating way.
    handleOpenDesktopMenu();
  }, [enrollment.id, client.id, navigate, handleOpenDesktopMenu]);

  const onCompletedMutation = useCallback(
    (status: AssessmentResponseStatus) => {
      if (!['saved', 'submitted'].includes(status)) return;
      // If we created a NEW assessment, evict Enrollment from the cache.
      if (!assessment) {
        cache.evict({ id: `Enrollment:${enrollment.id}` });
      }
      navigateToEnrollment();
    },
    [navigateToEnrollment, assessment, enrollment]
  );

  const { submitHandler, saveDraftHandler, mutationLoading, errors } =
    useAssessmentHandlers({
      formDefinitionId: editingDefinition.id,
      enrollmentId: enrollment.id,
      assessmentId: assessment?.id,
      assessmentLockVersion: assessment?.lockVersion,
      onCompletedMutation,
    });

  const showSaveWipButton = useMemo(() => {
    if (!editingDefinition.supportsSaveInProgress) return false;

    return !assessment || assessment.inProgress;
  }, [assessment, editingDefinition.supportsSaveInProgress]);

  const FormActionProps = useMemo(() => {
    return {
      onDiscard: navigateToEnrollment,
      stackOnMobile: true,
      config: [
        {
          id: 'submit',
          label: 'Submit',
          action: FormActionTypes.Submit,
          buttonProps: { variant: 'contained' } as const,
        },
        ...(showSaveWipButton
          ? [
              {
                id: 'saveDraft',
                label: 'Save and finish later',
                action: FormActionTypes.Save,
                buttonProps: { variant: 'outlined' } as const,
              },
            ]
          : []),
        {
          id: 'discard',
          label: 'Cancel',
          action: FormActionTypes.Discard,
          buttonProps: { variant: 'contained', color: 'grayscale' } as const,
        },
      ],
    };
  }, [navigateToEnrollment, showSaveWipButton]);

  return (
    <IndividualAssessment
      viewingDefinition={viewingDefinition}
      editingDefinition={editingDefinition}
      assessment={assessment}
      enrollmentId={enrollment.id}
      client={client}
      onSubmit={submitHandler}
      onSaveDraft={saveDraftHandler}
      errors={errors}
      mutationLoading={mutationLoading}
      FormActionProps={FormActionProps}
    />
  );
};

export default IndividualAssessmentFormController;
